import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import {
  Power, MapPin, Car, DollarSign, Clock,
  Phone, CheckCircle, Bike, Truck
} from "lucide-react";
import API from "../api/axiosConfig";
import MobileBottomBar from "../components/MobileBottomBar";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

// ── Helpers ────────────────────────────────────────────────────────────────
function makeIcon(emoji, size = 32) {
  return L.divIcon({
    html: `<div style="font-size:${size}px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">${emoji}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

const ryderIcon  = makeIcon("🚗", 36);
const originIcon = makeIcon("📍");
const destIcon   = makeIcon("🏁");

async function fetchRoute(from, to) {
  try {
    const res = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
    );
    const data = await res.json();
    const coords = data.routes?.[0]?.geometry?.coordinates;
    return coords ? coords.map(([lng, lat]) => [lat, lng]) : [];
  } catch {
    return [];
  }
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function playDoorbell() {
  navigator.vibrate?.([200, 100, 200]);
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(freq, start);
      osc.start(start);
      osc.stop(start + dur);
    };
    ctx.resume().then(() => {
      playTone(659, ctx.currentTime, 0.6);
      playTone(494, ctx.currentTime + 0.65, 0.8);
    });
  } catch {}
}

// ── MapFitter ──────────────────────────────────────────────────────────────
function MapFitter({ coords }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || coords.length < 2) return;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, { padding: [50, 50], animate: true });
    fitted.current = true;
  }, [coords, map]);
  return null;
}

// ── RideRequestCard ─────────────────────────────────────────────────────────
function RideRequestCard({ ride, myPos, onAccept, onDismiss }) {
  const [timeLeft, setTimeLeft] = useState(30);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(interval); onDismiss(ride.id); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onDismiss, ride.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const distanceToPickup = myPos
    ? Math.round(haversineKm(myPos.lat, myPos.lng, Number(ride.originLat), Number(ride.originLng)) * 100) / 100
    : ride.distanceToPickupKm ?? "?";

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-supernova-200 p-4 mb-3 relative overflow-hidden">
      {/* Barra de tiempo */}
      <div
        className="absolute top-0 left-0 h-1 bg-supernova-400 transition-all duration-1000"
        style={{ width: `${(timeLeft / 30) * 100}%` }}
      />

      <div className="flex items-start justify-between gap-3 mt-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span className="truncate">{ride.originAddress}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <div className="w-2.5 h-2.5 rounded-sm bg-red-500" />
            <span className="truncate">{ride.destAddress}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-supernova-600 font-bold text-lg">
            ${Number(ride.estimatedFare).toLocaleString("es-CO")}
          </p>
          <p className="text-xs text-gray-400">{distanceToPickup} km de ti</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Clock size={12} /> {timeLeft}s
        </span>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Car size={12} /> {Number(ride.distanceKm).toFixed(1)} km de viaje
        </span>
        {ride.vehicleType && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-supernova-100 text-supernova-700 flex items-center gap-1">
            {ride.vehicleType === "moto" && <Bike size={11} />}
            {ride.vehicleType === "carro" && <Car size={11} />}
            {ride.vehicleType === "camioneta" && <Truck size={11} />}
            {ride.vehicleType}
          </span>
        )}
      </div>

      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onDismiss(ride.id)}
          className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 transition"
        >
          Rechazar
        </button>
        <button
          onClick={() => onAccept(ride.id)}
          className="flex-2 flex-1 py-2.5 rounded-xl bg-supernova-500 text-white text-sm font-bold hover:opacity-90 active:scale-95 transition"
        >
          Aceptar viaje
        </button>
      </div>
    </div>
  );
}

// ── TransportRyderPage ─────────────────────────────────────────────────────
export default function TransportRyderPage() {
  const token = localStorage.getItem("accessToken") || localStorage.getItem("token");

  const [online, setOnline]           = useState(false);
  const [myPos, setMyPos]             = useState(null);
  const [requests, setRequests]       = useState([]);
  const [activeRide, setActiveRide]   = useState(null);
  const [routeCoords, setRouteCoords] = useState([]);
  const [stats, setStats]             = useState({ today: 0, earnings: 0 });
  const [ryderVehicleType, setRyderVehicleType] = useState(null);

  const socketRef      = useRef(null);
  const locationInterval = useRef(null);

  // ── Cargar viaje activo y perfil del ryder al montar ────────────────────
  useEffect(() => {
    API.get("/transport/active")
      .then((res) => {
        const ride = res.data?.data?.ride;
        if (ride && !["completado", "cancelado"].includes(ride.status)) {
          setActiveRide(ride);
          setOnline(true);
        }
      })
      .catch(() => {});

    API.get("/ryders/me")
      .then((res) => {
        const ryder = res.data?.data?.ryder ?? res.data?.data;
        if (ryder?.vehicleType) setRyderVehicleType(ryder.vehicleType);
      })
      .catch(() => {});

    API.get("/transport/ryder/history", { params: { page: 1, limit: 100 } })
      .then((res) => {
        const rides = res.data?.data?.rides ?? [];
        const today = new Date().toDateString();
        const todayRides = rides.filter(
          (r) => r.status === "completado" && new Date(r.completedAt).toDateString() === today
        );
        const earnings = todayRides.reduce((sum, r) => sum + Number(r.finalFare || r.estimatedFare), 0);
        setStats({ today: todayRides.length, earnings });
      })
      .catch(() => {});
  }, []);

  // ── Socket.io ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      auth: { token },
    });
    socketRef.current = socket;

    socket.on("transport:request_new", (data) => {
      if (!online) return;
      // Filtrar: si el viaje pide un tipo específico, solo aceptarlo si coincide con el ryder
      const rideVehicleType = data.ride?.vehicleType;
      if (rideVehicleType && ryderVehicleType && rideVehicleType !== ryderVehicleType) return;
      playDoorbell();
      setRequests((prev) => {
        if (prev.find((r) => r.id === data.ride.id)) return prev;
        return [data.ride, ...prev];
      });
    });

    socket.on("transport:status_change", ({ status }) => {
      setActiveRide((prev) => prev ? { ...prev, status } : prev);
    });

    return () => socket.disconnect();
  }, [token, online, ryderVehicleType]);

  // Suscribirse a sala del viaje activo
  useEffect(() => {
    if (activeRide?.id && socketRef.current) {
      socketRef.current.emit("transport:subscribe", activeRide.id);
    }
  }, [activeRide?.id]);

  // ── GPS: emitir posición cuando está online ──────────────────────────────
  useEffect(() => {
    if (!online) {
      clearInterval(locationInterval.current);
      return;
    }

    const updateLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMyPos(coords);
          API.patch("/ryders/me/location", coords).catch(() => {});
          socketRef.current?.emit("transport:ryder_location", coords);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    };

    updateLocation();
    locationInterval.current = setInterval(updateLocation, 5000);
    return () => clearInterval(locationInterval.current);
  }, [online]);

  // ── Cargar solicitudes cercanas al conectarse ────────────────────────────
  useEffect(() => {
    if (!online) return;
    API.get("/transport/nearby")
      .then((res) => {
        const rides = res.data?.data?.rides ?? [];
        setRequests(rides);
      })
      .catch(() => {});
  }, [online]);

  // ── Ruta hacia el punto relevante del viaje activo ────────────────────────
  useEffect(() => {
    if (!activeRide || !myPos) return;
    const target = activeRide.status === "en_viaje"
      ? { lat: Number(activeRide.destLat), lng: Number(activeRide.destLng) }
      : { lat: Number(activeRide.originLat), lng: Number(activeRide.originLng) };
    fetchRoute(myPos, target).then(setRouteCoords);
  }, [activeRide, myPos]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toggle online/offline ─────────────────────────────────────────────────
  const handleToggleOnline = async () => {
    const newStatus = !online;
    try {
      await API.patch("/ryders/me/status", { status: newStatus ? "disponible" : "offline" });
      setOnline(newStatus);
      if (!newStatus) { setRequests([]); }
      toast.success(newStatus ? "¡Estás en línea y listo para recibir viajes!" : "Te desconectaste");
    } catch {
      toast.error("Error al cambiar tu estado");
    }
  };

  // ── Aceptar viaje ─────────────────────────────────────────────────────────
  const handleAccept = async (rideId) => {
    try {
      const res = await API.patch(`/transport/${rideId}/accept`);
      const ride = res.data?.data?.ride;
      setActiveRide(ride);
      setRequests([]);
      toast.success("¡Viaje aceptado! Ve al punto de recogida");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al aceptar el viaje");
    }
  };

  // ── Avanzar estado ────────────────────────────────────────────────────────
  const handleStatusUpdate = async (newStatus) => {
    if (!activeRide) return;
    try {
      const res = await API.patch(`/transport/${activeRide.id}/status`, { status: newStatus });
      setActiveRide(res.data?.data?.ride);
      if (newStatus === "completado") {
        toast.success("¡Viaje completado!");
        setActiveRide(null);
        setRouteCoords([]);
        setStats((s) => ({
          today: s.today + 1,
          earnings: s.earnings + Number(activeRide.estimatedFare),
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al actualizar estado");
    }
  };

  const defaultCenter = myPos ? [myPos.lat, myPos.lng] : [8.0, -66.0]; // Venezuela

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 60px)" }}>
      {/* ── MAPA ── */}
      <div className="flex-1 relative z-0">
        <MapContainer center={defaultCenter} zoom={14} className="h-full w-full" zoomControl={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          />
          {myPos && <Marker position={[myPos.lat, myPos.lng]} icon={ryderIcon} />}
          {activeRide && (
            <>
              <Marker
                position={[Number(activeRide.originLat), Number(activeRide.originLng)]}
                icon={originIcon}
              />
              <Marker
                position={[Number(activeRide.destLat), Number(activeRide.destLng)]}
                icon={destIcon}
              />
            </>
          )}
          {routeCoords.length > 1 && (
            <Polyline positions={routeCoords} color="#7c3aed" weight={5} opacity={0.85} />
          )}
          {routeCoords.length > 1 && <MapFitter coords={routeCoords} />}
        </MapContainer>

        {/* Indicador de estado — flotante sobre el mapa */}
        <div
          className={`absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg font-semibold text-xs pointer-events-none ${
            online
              ? "bg-green-500 text-white"
              : "bg-white text-gray-500 border border-gray-200"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${online ? "bg-white animate-pulse" : "bg-gray-300"}`} />
          {online ? "En línea" : "Desconectado"}
        </div>
      </div>

      {/* ── PANEL INFERIOR ── */}
      <div className="bg-white shadow-2xl rounded-t-3xl z-10 max-h-[60vh] overflow-y-auto">
        <div className="p-5 pb-20 lg:pb-8">
          <div className="w-10 h-1 bg-gray-300 rounded mx-auto mb-4" />

          {/* ── Offline ── */}
          {!online && !activeRide && (
            <div className="py-4">
              {/* Stats del día */}
              <div className="flex gap-3 mb-5">
                <div className="flex-1 bg-supernova-50 rounded-2xl px-4 py-3 text-center">
                  <p className="text-2xl font-bold text-supernova-600">{stats.today}</p>
                  <p className="text-xs text-gray-500">Viajes hoy</p>
                </div>
                <div className="flex-1 bg-green-50 rounded-2xl px-4 py-3 text-center">
                  <p className="text-2xl font-bold text-green-600">
                    ${stats.earnings.toLocaleString("es-CO")}
                  </p>
                  <p className="text-xs text-gray-500">Ganancias hoy</p>
                </div>
              </div>

              {/* CTA principal — grande y visible */}
              <button
                onClick={handleToggleOnline}
                className="w-full py-4 rounded-2xl bg-supernova-500 text-white font-bold text-lg flex items-center justify-center gap-3 hover:bg-supernova-600 active:scale-95 transition shadow-lg shadow-supernova-200"
              >
                <Power size={22} />
                Conectarme para recibir viajes
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">
                Activa tu disponibilidad para comenzar a recibir solicitudes
              </p>
            </div>
          )}

          {/* ── Online: solicitudes entrantes ── */}
          {online && !activeRide && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  {requests.length > 0 ? `${requests.length} solicitudes` : "Esperando solicitudes..."}
                </h3>
                <button
                  onClick={handleToggleOnline}
                  className="flex items-center gap-1.5 text-xs text-red-500 font-semibold bg-red-50 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-100 transition"
                >
                  <Power size={13} />
                  Desconectarme
                </button>
              </div>

              {requests.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🚗</div>
                  <p className="text-gray-400 text-sm">Las solicitudes de viaje aparecerán aquí</p>
                </div>
              )}

              {requests.map((ride) => (
                <RideRequestCard
                  key={ride.id}
                  ride={ride}
                  myPos={myPos}
                  onAccept={handleAccept}
                  onDismiss={(id) => setRequests((prev) => prev.filter((r) => r.id !== id))}
                />
              ))}
            </>
          )}

          {/* ── Viaje activo ── */}
          {activeRide && (
            <div>
              {/* Estado */}
              <div className={`rounded-xl px-4 py-2.5 mb-4 text-center text-sm font-semibold ${
                activeRide.status === "en_viaje"
                  ? "bg-green-100 text-green-700"
                  : activeRide.status === "recogiendo"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-supernova-100 text-supernova-700"
              }`}>
                {activeRide.status === "aceptado"   && "Ve al punto de recogida"}
                {activeRide.status === "recogiendo" && "Esperando al pasajero"}
                {activeRide.status === "en_viaje"   && "Llevando al pasajero al destino"}
              </div>

              {/* Pasajero */}
              {activeRide.user && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-supernova-100 flex items-center justify-center text-xl shrink-0">
                    {activeRide.user.avatar
                      ? <img src={activeRide.user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                      : "👤"}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-800">{activeRide.user.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <MapPin size={12} />
                      <span className="truncate">
                        {activeRide.status === "en_viaje"
                          ? activeRide.destAddress
                          : activeRide.originAddress}
                      </span>
                    </div>
                  </div>
                  {activeRide.user.phone && (
                    <a
                      href={`tel:${activeRide.user.phone}`}
                      className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white hover:bg-green-600 shrink-0"
                    >
                      <Phone size={18} />
                    </a>
                  )}
                </div>
              )}

              {/* Tarifa */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 mb-4">
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Car size={15} />
                  <span>{Number(activeRide.distanceKm).toFixed(1)} km</span>
                </div>
                <div className="flex items-center gap-1 text-supernova-600 font-bold">
                  <DollarSign size={15} />
                  {Number(activeRide.estimatedFare).toLocaleString("es-CO")}
                </div>
              </div>

              {/* Botones de progresión */}
              {activeRide.status === "aceptado" && (
                <button
                  onClick={() => handleStatusUpdate("recogiendo")}
                  className="w-full py-3.5 rounded-2xl bg-blue-500 text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition"
                >
                  <CheckCircle size={20} />
                  Llegué al punto de recogida
                </button>
              )}
              {activeRide.status === "recogiendo" && (
                <button
                  onClick={() => handleStatusUpdate("en_viaje")}
                  className="w-full py-3.5 rounded-2xl bg-supernova-500 text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition"
                >
                  <Car size={20} />
                  Pasajero a bordo — Iniciar viaje
                </button>
              )}
              {activeRide.status === "en_viaje" && (
                <button
                  onClick={() => handleStatusUpdate("completado")}
                  className="w-full py-3.5 rounded-2xl bg-green-500 text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition"
                >
                  <CheckCircle size={20} />
                  Viaje completado
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <MobileBottomBar />
    </div>
  );
}
